import React, { useEffect, useRef, useState } from 'react';
import { match, P } from 'ts-pattern';
import { useLocation } from 'wouter';
import { DeviceSize, useDeviceSize } from 'wrappers/DeviceSizeProvider';

import { BoxCharacters } from '@fragment/types/boxChars';
import { TextButton } from '@fragment/ui/src/components/Button/TextButton/TextButton';
import { Dropdown } from '@fragment/ui/src/components/Dropdown/Dropdown';
import { Link } from '@fragment/ui/src/components/Link';

export const truncateBreadcrumbName = (name: string, maxLength = 16) => {
  if (name.length <= maxLength) {
    return name;
  }

  // prevent ending the truncated section with special characters that will look odd
  const truncatedName = name.slice(0, maxLength);
  if (
    truncatedName.slice(-1) === ':' ||
    truncatedName.slice(-1) === ' ' ||
    truncatedName.slice(-1) === '.'
  ) {
    return `${truncatedName.slice(0, -1)}…`;
  }
  return `${truncatedName}…`;
};

export type BreadcrumbElement = {
  name: string;
  url?: string;
  icon?: React.ReactNode;
};

export const BreadcrumbLink = ({
  name,
  url,
  shouldTruncate = true,
  truncateLength = 16,
  icon,
}: BreadcrumbElement & {
  shouldTruncate?: boolean;
  truncateLength?: number;
}) => (
  <>
    {url ? (
      <Link
        className="text-main-500 hover:text-main flex items-center gap-[1px] whitespace-nowrap "
        key={url}
        href={url}
      >
        {icon}
        {`${
          shouldTruncate ? truncateBreadcrumbName(name, truncateLength) : name
        }`}
      </Link>
    ) : (
      <span className="text-main-500 flex items-center gap-[1px]">
        {icon}
        {`${
          shouldTruncate ? truncateBreadcrumbName(name, truncateLength) : name
        }`}
      </span>
    )}
  </>
);

export const BreadcrumbDropdown = ({
  placeholder = '…',
  elements,
  truncateLength = 16,
  showIcon = false,
}: {
  placeholder?: string;
  elements: BreadcrumbElement[];
  truncateLength?: number;
  showIcon?: boolean;
}) => {
  const [, setLocation] = useLocation();
  const truncatedPlaceholder = truncateBreadcrumbName(
    placeholder,
    truncateLength
  );
  return (
    <>
      <Dropdown
        displayValue={truncatedPlaceholder}
        options={elements.map(({ name, url }, i) => ({
          label: `${
            i === elements.length - 1
              ? BoxCharacters.RIGHT_BOTTOM_ELBOW
              : BoxCharacters.RIGHT_TEE
          }${truncateBreadcrumbName(name, truncateLength)}`,
          value: url ?? '',
        }))}
        onChange={(selected) => {
          if (selected !== '') setLocation(selected);
        }}
        placeholder={
          <span className="text-main-500 hover:text-main">
            {truncatedPlaceholder}
          </span>
        }
        hoverBackgroundColor="bg-canvas"
        size="md"
        width="w-fit"
        showIcon={showIcon}
        data-testid="breadcrumb-dropdown"
      />
    </>
  );
};

type BreadcrumbErrorProps = {
  baseElement: BreadcrumbElement;
  retry: () => void;
  fetching: boolean;
};
export const BreadcrumbError = ({
  baseElement,
  retry,
  fetching,
}: BreadcrumbErrorProps) => (
  <div className="flex flex-row items-center text-main-500">
    <BreadcrumbLink
      name={baseElement.name}
      icon={baseElement.icon}
      shouldTruncate={false}
      truncateLength={16}
    />
    /
    <TextButton extraClassNames="translate-y-[1px]" onClick={retry}>
      {fetching ? '...' : '↺'}
    </TextButton>
  </div>
);

type BreadcrumbProps = {
  elements: BreadcrumbElement[];
  showLoadingText?: boolean;
};

export const BreadcrumbLoader = () => {
  const loadingIndex = useRef(1);
  const [loadingText, setLoadingText] = useState('');
  useEffect(() => {
    const interval = setInterval(() => {
      loadingIndex.current = (loadingIndex.current + 1) % 7;
      const text = new Array<string>(loadingIndex.current).fill('_').join('');
      setLoadingText(`/${text}/`);
    }, 100);
    return () => clearInterval(interval);
  }, []);
  return (
    <span className="text-main-500" data-testid="breadcrumb-loader">
      {loadingText}
    </span>
  );
};

export const BreadcrumbComponent = ({
  elements,
  showLoadingText = false,
}: BreadcrumbProps) => {
  const device = useDeviceSize();
  // truncate breadcrumb elements based on device size
  const truncateLength = match(device)
    .with(DeviceSize.Phone, () => 8)
    .with(DeviceSize.Tablet, () => 12)
    .with(DeviceSize.WideTablet, () => 10)
    .with(DeviceSize.Laptop, () => 16)
    .with(DeviceSize.LargeLaptop, () => 16)
    .with(DeviceSize.Desktop, () => 20)
    .exhaustive();

  // separate breadcrumbs for different devices
  let content;
  if (device === DeviceSize.Tablet || device === DeviceSize.Phone) {
    const elementsReversed = [...elements].reverse();
    content = match(elements.length)
      .with(1, () => (
        <BreadcrumbLink
          name={elements[0].name}
          icon={elements[0].icon}
          shouldTruncate={false}
          truncateLength={truncateLength}
        />
      ))
      .otherwise(() => (
        <BreadcrumbDropdown
          elements={elementsReversed.slice(1, elementsReversed.length)}
          truncateLength={truncateLength}
          placeholder={elementsReversed[0].name}
          showIcon
        />
      ));
  } else {
    content = match(elements.length)
      .with(1, () => (
        <BreadcrumbLink
          name={elements[0].name}
          icon={elements[0].icon}
          shouldTruncate={false}
        />
      ))
      .with(P.number.lte(4), () => (
        <div className="flex flex-row">
          {elements.map((element, i) => (
            <React.Fragment key={`${element.url}-${element.name}`}>
              <BreadcrumbLink
                name={element.name}
                url={i === elements.length - 1 ? undefined : element.url}
                icon={element.icon}
                truncateLength={truncateLength}
              />
              {i < elements.length - 1 && (
                <span className="text-main-500">/</span>
              )}
            </React.Fragment>
          ))}
        </div>
      ))
      .otherwise(() => (
        <div className="flex flex-row">
          <BreadcrumbLink
            key={elements[0].url}
            name={elements[0].name}
            url={elements[0].url}
            icon={elements[0].icon}
            truncateLength={truncateLength}
          />
          <span className="text-main-500">/</span>
          <div className="flex items-center gap-[1px]">
            {elements[1].icon}
            <BreadcrumbDropdown
              elements={elements.slice(1, elements.length - 2)}
              truncateLength={truncateLength}
            />
          </div>
          <span className="text-main-500">/</span>
          {elements.slice(-2).map((element, i) => (
            <React.Fragment key={`${element.url}-${element.name}`}>
              <BreadcrumbLink
                name={element.name}
                url={i === 0 ? element.url : undefined}
                icon={element.icon}
                truncateLength={truncateLength}
              />
              {i === 0 && <span className="text-main-500">/</span>}
            </React.Fragment>
          ))}
        </div>
      ));
  }

  return (
    <div className="flex flex-row" data-testid="breadcrumb-component">
      {content}
      {showLoadingText && <BreadcrumbLoader />}
    </div>
  );
};
